import React, { useState, useEffect } from 'react'
import { api } from '../../utils/api'
import { PRODUCT_TYPES } from '../../utils/constants'
import UserEditModal from './UserEditModal'

function getValidationBadge(log) {
  if (!log) return null
  if (log.includes('⚠ MODEL VALIDATION WARNING') || log.includes('⚠ LOW IDENTITY CONFIDENCE')) {
    return { className: 'validation-badge warning', label: '⚠ Validation Warning' }
  }
  if (log.includes('--- BLOCKED ---')) {
    return { className: 'validation-badge blocked', label: 'Blocked (legacy)' }
  }
  return { className: 'validation-badge passed', label: '✓ Validated' }
}

function parsePricingLogSections(log) {
  if (!log) return []
  const sectionDefs = [
    { marker: /^\[\d{4}-\d{2}-\d{2}/, title: 'CLAUDE PRICING LOG' },
    { marker: /^Status:\s*CONFIRMED|^Visible Text:|^---\s*MODEL VALIDATION\s*---/m, title: 'MODEL VALIDATION' },
    { marker: /^IC:\s|^---\s*CONFIDENCE SCORES\s*---/m, title: 'CONFIDENCE SCORES' },
    { marker: /^Score:\s*\d+\s*\(|^---\s*DATASET CONFIDENCE SCORE\s*---/m, title: 'DATASET CONFIDENCE SCORE' },
    { marker: /^Total filtered:|^---\s*CONDITION SEGMENTATION\s*---/m, title: 'CONDITION SEGMENTATION' },
    { marker: /^Method:\s*(AI|Rule)|^---\s*SEGMENT CLASSIFICATION\s*---/m, title: 'SEGMENT CLASSIFICATION' },
    { marker: /^Text SKUs:|^---\s*RECONCILIATION\s*---/m, title: 'RECONCILIATION' },
    { marker: /^---\s*SMALL SAMPLE|^Cleaned sold|^---\s*PRICE BUCKETING ANALYSIS\s*---/m, title: 'PRICE BUCKETING ANALYSIS' },
    { marker: /^Intake condition:|^Baseline price|^---\s*CONDITION ADJUSTMENT\s*---/m, title: 'CONDITION ADJUSTMENT' },
    { marker: /^Sold Prices \(shipped\):|^Active BIN:|^---\s*EBAY UK SOLD/m, title: 'EBAY UK SOLD' },
    { marker: /^Used\/eBay:|^---\s*GOOGLE SHOPPING/m, title: 'GOOGLE SHOPPING' },
    { marker: /^Amazon:|^---\s*AMAZON UK/m, title: 'AMAZON UK' },
    { marker: /^Sold:.*Active:|^Collection-only \(local|^---\s*REVERB UK/m, title: 'REVERB UK' },
    { marker: /^Intake brand:|^Model compare:|^---\s*MODEL VALIDATION GATE\s*---/m, title: 'MODEL VALIDATION GATE' },
    { marker: /^R2 lookup:|^Found \d+ images|^Video frames:|^\[AZURE-OCR\]|^---\s*IMAGE DEBUG\s*---/m, title: 'IMAGE DEBUG' },
  ]
  const lines = log.split('\n')
  const sections = []
  let currentTitle = 'CLAUDE PRICING LOG'
  let currentLines = []
  for (const line of lines) {
    if (/^---\s*[A-Z].*---\s*$/.test(line.trim())) {
      let matched = false
      for (const def of sectionDefs) {
        if (def.title !== currentTitle && def.marker.test(line)) {
          if (currentLines.length > 0) sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
          currentTitle = def.title
          currentLines = []
          matched = true
          break
        }
      }
      if (!matched) continue
      continue
    }
    if (line.trim() === '---') continue
    let matched = false
    for (const def of sectionDefs) {
      if (def.title !== currentTitle && def.marker.test(line)) {
        if (currentLines.length > 0) sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
        currentTitle = def.title
        currentLines = [line]
        matched = true
        break
      }
    }
    if (!matched) currentLines.push(line)
  }
  if (currentLines.length > 0) sections.push({ title: currentTitle, content: currentLines.join('\n').trim() })
  const merged = []
  const seen = new Set()
  for (const sec of sections) {
    if (seen.has(sec.title)) {
      const existing = merged.find(m => m.title === sec.title)
      if (existing) existing.content += '\n' + sec.content
    } else {
      seen.add(sec.title)
      merged.push({ ...sec })
    }
  }
  return merged.filter(s => s.content)
}

function AdminPanel() {
  const [activeTab, setActiveTab] = useState('users')
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [editingUser, setEditingUser] = useState(null)
  const [creatingUser, setCreatingUser] = useState(false)
  const [newUserPin, setNewUserPin] = useState(null)
  const [roles, setRoles] = useState([])

  // Settings state
  const [settings, setSettings] = useState([])
  const [settingsLoading, setSettingsLoading] = useState(false)
  const [settingsSaving, setSettingsSaving] = useState(false)

  // Video Assess state
  const [videoFile, setVideoFile] = useState(null)
  const [compressing, setCompressing] = useState(false)
  const [compressProgress, setCompressProgress] = useState(0)
  const [extractingFrames, setExtractingFrames] = useState(false)
  const [keyframes, setKeyframes] = useState([])
  const [numKeyframes, setNumKeyframes] = useState(8)
  const [assessing, setAssessing] = useState(false)
  const [assessmentResult, setAssessmentResult] = useState(null)
  const [videoStatus, setVideoStatus] = useState('')
  const [showPricingLog, setShowPricingLog] = useState(false)
  const [lightboxFrame, setLightboxFrame] = useState(null) // index of frame to show in lightbox

  // Title Cleanup state (Trac #224)
  const [titleProducts, setTitleProducts] = useState([])
  const [titleResults, setTitleResults] = useState([])
  const [titleScanning, setTitleScanning] = useState(false)
  const [titleAnalysing, setTitleAnalysing] = useState(false)
  const [titleApplying, setTitleApplying] = useState(false)
  const [titleProgress, setTitleProgress] = useState({ done: 0, total: 0 })
  const [titleEditingId, setTitleEditingId] = useState(null)
  const [titleEditValue, setTitleEditValue] = useState('')

  // Technician state
  const [technicians, setTechnicians] = useState([])
  const [techLoading, setTechLoading] = useState(false)
  const [editingTech, setEditingTech] = useState(null) // null = closed, {} = new, {id:...} = editing
  const [techForm, setTechForm] = useState({ name: '', email: '', active: true, product_types: [] })
  const [techSaving, setTechSaving] = useState(false)
  const [techSearch, setTechSearch] = useState('')

  useEffect(() => {
    loadUsers()
    loadRoles()
  }, [])

  useEffect(() => {
    if (activeTab === 'technicians' && technicians.length === 0) {
      loadTechnicians()
    }
    if (activeTab === 'settings' && settings.length === 0) {
      loadSettings()
    }
  }, [activeTab])

  async function loadUsers() {
    setLoading(true)
    try {
      const response = await api.get('/api/users')
      setUsers(response || [])
      setError(null)
    } catch (err) {
      setError('Failed to load users')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function loadRoles() {
    try {
      const response = await api.get('/api/users/roles')
      setRoles(response || [])
    } catch (err) {
      console.error('Failed to load roles', err)
    }
  }

  async function loadSettings() {
    setSettingsLoading(true)
    try {
      const response = await api.get('/api/admin/pricing-settings')
      setSettings(response.settings || [])
    } catch (err) {
      console.error('Failed to load settings', err)
    } finally {
      setSettingsLoading(false)
    }
  }

  async function updateSetting(key, value) {
    setSettingsSaving(true)
    try {
      await api.post('/api/admin/pricing-settings', { key, value })
      loadSettings()
    } catch (err) {
      alert(err.message || 'Failed to update setting')
    } finally {
      setSettingsSaving(false)
    }
  }

  function getSettingValue(key, defaultValue = '') {
    const setting = settings.find(s => s.key === key)
    return setting ? setting.value : defaultValue
  }

  async function handleResetPin(userId, userName) {
    if (!confirm(`Regenerate PIN for ${userName}? Their current PIN will stop working.`)) return

    try {
      const response = await api.post(`/api/users/${userId}/pin/regenerate`)
      if (response.pin) {
        setNewUserPin({ userId, userName, pin: response.pin })
      } else {
        alert('Failed to regenerate PIN')
      }
    } catch (err) {
      alert(err.message || 'Failed to regenerate PIN')
      console.error(err)
    }
  }

  async function handleViewPin(userId, userName) {
    try {
      const response = await api.get(`/api/users/${userId}/pin`)
      if (response.pin) {
        setNewUserPin({ userId, userName, pin: response.pin })
      } else {
        alert('No active PIN found')
      }
    } catch (err) {
      alert(err.message || 'Failed to view PIN')
      console.error(err)
    }
  }

  async function handleDisable(userId, userName) {
    const reason = prompt(`Reason for deactivating ${userName}:`)
    if (!reason) return

    try {
      await api.post(`/api/users/${userId}/disable`, { reason })
      loadUsers()
    } catch (err) {
      alert(err.message || 'Failed to deactivate user')
      console.error(err)
    }
  }

  async function handleUnlock(userId, userName) {
    if (!confirm(`Unlock ${userName}? This resets failed login attempts.`)) return

    try {
      await api.post(`/api/users/${userId}/unlock`)
      alert(`${userName} has been unlocked`)
    } catch (err) {
      alert(err.message || 'Failed to unlock user')
      console.error(err)
    }
  }

  function getRoleNames(user) {
    if (user.roles && user.roles.length > 0) {
      return user.roles.map(r => r.name)
    }
    return user.is_admin ? ['Admin'] : []
  }

  // ---- Technician functions ----

  async function loadTechnicians() {
    setTechLoading(true)
    try {
      const response = await api.get('/api/warehouse/technicians')
      setTechnicians(response.technicians || [])
    } catch (err) {
      console.error('Failed to load technicians', err)
    } finally {
      setTechLoading(false)
    }
  }

  function openTechForm(tech) {
    if (tech) {
      setTechForm({ name: tech.name, email: tech.email || '', active: !!tech.active, product_types: tech.product_types || [] })
      setEditingTech(tech)
    } else {
      setTechForm({ name: '', email: '', active: true, product_types: [] })
      setEditingTech({})
    }
  }

  async function saveTechnician() {
    if (!techForm.name.trim()) {
      alert('Name is required')
      return
    }
    setTechSaving(true)
    try {
      if (editingTech.id) {
        await api.patch(`/api/warehouse/technicians/${editingTech.id}`, techForm)
      } else {
        await api.post('/api/warehouse/technicians', techForm)
      }
      setEditingTech(null)
      loadTechnicians()
    } catch (err) {
      alert('Failed to save technician')
      console.error(err)
    } finally {
      setTechSaving(false)
    }
  }

  async function toggleTechActive(tech) {
    const action = tech.active ? 'deactivate' : 'reactivate'
    if (!confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${tech.name}?`)) return
    try {
      await api.patch(`/api/warehouse/technicians/${tech.id}`, { active: !tech.active })
      loadTechnicians()
    } catch (err) {
      alert(`Failed to ${action} technician`)
    }
  }

  function toggleProductType(pt) {
    setTechForm(prev => {
      const types = prev.product_types.includes(pt)
        ? prev.product_types.filter(t => t !== pt)
        : [...prev.product_types, pt]
      return { ...prev, product_types: types }
    })
  }

  const filteredProductTypes = PRODUCT_TYPES.filter(pt =>
    pt.toLowerCase().includes(techSearch.toLowerCase())
  )

  // ---- Video Assess functions ----

  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (document.querySelector(`script[src="${src}"]`)) return resolve()
      const script = document.createElement('script')
      script.src = src
      script.onload = resolve
      script.onerror = reject
      document.head.appendChild(script)
    })
  }

  async function getFFmpeg() {
    if (window._ffmpegInstance && window._ffmpegLoaded) return window._ffmpegInstance
    await loadScript('https://cdn.jsdelivr.net/npm/@ffmpeg/ffmpeg@0.12.10/dist/umd/ffmpeg.min.js')
    await loadScript('https://cdn.jsdelivr.net/npm/@ffmpeg/util@0.12.1/dist/umd/util.min.js')
    const { FFmpeg } = window.FFmpegWASM
    const ffmpeg = new FFmpeg()
    ffmpeg.on('progress', ({ progress }) => {
      setCompressProgress(Math.round(progress * 100))
    })
    await ffmpeg.load({
      coreURL: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.js',
      wasmURL: 'https://cdn.jsdelivr.net/npm/@ffmpeg/core@0.12.6/dist/umd/ffmpeg-core.wasm'
    })
    window._ffmpegInstance = ffmpeg
    window._ffmpegLoaded = true
    return ffmpeg
  }

  async function compressVideo(file) {
    const ffmpeg = await getFFmpeg()
    const { fetchFile } = window.FFmpegUtil
    const inputName = 'input' + (file.name.match(/\.\w+$/)?.[0] || '.mp4')
    const outputName = 'output.mp4'
    await ffmpeg.writeFile(inputName, await fetchFile(file))
    await ffmpeg.exec([
      '-i', inputName,
      '-vf', 'scale=-2:720',
      '-c:v', 'libx264',
      '-preset', 'fast',
      '-crf', '28',
      '-c:a', 'aac',
      '-b:a', '128k',
      '-movflags', '+faststart',
      '-y', outputName
    ])
    const data = await ffmpeg.readFile(outputName)
    return new File([data.buffer], 'compressed.mp4', { type: 'video/mp4' })
  }

  async function extractFrames(file, count) {
    const blobUrl = URL.createObjectURL(file)
    try {
      return await new Promise((resolve, reject) => {
        const video = document.createElement('video')
        video.muted = true
        video.preload = 'auto'
        const timeout = setTimeout(() => {
          video.src = ''
          reject(new Error('Video frame extraction timed out'))
        }, 30000)

        video.onloadedmetadata = () => {
          const duration = video.duration
          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          canvas.width = Math.min(video.videoWidth, 1280)
          canvas.height = Math.round(canvas.width * (video.videoHeight / video.videoWidth))

          const frames = []
          const times = []
          for (let i = 0; i < count; i++) {
            times.push(count === 1 ? duration * 0.25 : (duration * i) / (count - 1))
          }

          let currentFrame = 0
          video.onseeked = () => {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
            const dataUrl = canvas.toDataURL('image/jpeg', 0.85)
            frames.push(dataUrl.split(',')[1])
            currentFrame++
            setVideoStatus(`Extracting frame ${currentFrame}/${count}...`)

            if (currentFrame < times.length) {
              video.currentTime = times[currentFrame]
            } else {
              clearTimeout(timeout)
              video.src = ''
              resolve(frames)
            }
          }
          video.currentTime = times[0]
        }

        video.onerror = () => {
          clearTimeout(timeout)
          reject(new Error('Failed to load video'))
        }
        video.src = blobUrl
      })
    } finally {
      URL.revokeObjectURL(blobUrl)
    }
  }

  async function handleVideoUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return

    setVideoFile(file)
    setKeyframes([])
    setAssessmentResult(null)
    setShowPricingLog(false)

    let processFile = file
    const sizeMB = file.size / (1024 * 1024)

    // Compress if > 20MB
    if (sizeMB > 20) {
      setCompressing(true)
      setCompressProgress(0)
      setVideoStatus(`Compressing video (${sizeMB.toFixed(0)}MB)...`)
      try {
        processFile = await compressVideo(file)
        const newSizeMB = processFile.size / (1024 * 1024)
        setVideoStatus(`Compressed ${sizeMB.toFixed(0)}MB → ${newSizeMB.toFixed(1)}MB`)
      } catch (err) {
        console.error('Compression failed, using original:', err)
        setVideoStatus('Compression failed, using original file')
        processFile = file
      } finally {
        setCompressing(false)
      }
    }

    // Extract keyframes
    setExtractingFrames(true)
    setVideoStatus('Extracting keyframes...')
    try {
      const frames = await extractFrames(processFile, numKeyframes)
      setKeyframes(frames)
      setVideoStatus(`${frames.length} keyframes extracted`)
    } catch (err) {
      console.error('Frame extraction failed:', err)
      setVideoStatus('Frame extraction failed: ' + err.message)
    } finally {
      setExtractingFrames(false)
    }
  }

  async function assessFrames() {
    if (keyframes.length === 0) return
    setAssessing(true)
    setAssessmentResult(null)
    setVideoStatus('Sending frames to pricing engine...')
    try {
      const result = await api.post('/api/deals/assess-video-frames', { frames: keyframes })
      setAssessmentResult(result)
      setVideoStatus(result.success ? 'Assessment complete' : `Assessment failed: ${result.error}`)
    } catch (err) {
      console.error('Assessment failed:', err)
      setVideoStatus('Assessment failed: ' + err.message)
      setAssessmentResult({ success: false, error: err.message })
    } finally {
      setAssessing(false)
    }
  }

  function resetVideoAssess() {
    setVideoFile(null)
    setKeyframes([])
    setAssessmentResult(null)
    setVideoStatus('')
    setCompressing(false)
    setExtractingFrames(false)
    setAssessing(false)
    setShowPricingLog(false)
  }

  // === Title Cleanup Handlers (Trac #224) ===

  const handleTitleScan = async () => {
    setTitleScanning(true)
    setTitleProducts([])
    setTitleResults([])
    try {
      const data = await api.post('/api/inventory/title-scan', {})
      if (data.success) {
        setTitleProducts(data.products)
      } else {
        alert('Scan failed: ' + (data.error || 'Unknown error'))
      }
    } catch (e) {
      alert('Scan error: ' + e.message)
    } finally {
      setTitleScanning(false)
    }
  }

  const handleTitleAnalyse = async () => {
    if (!titleProducts.length) return
    setTitleAnalysing(true)
    setTitleResults([])
    setTitleProgress({ done: 0, total: titleProducts.length })

    const batchSize = 10
    const allResults = []

    for (let i = 0; i < titleProducts.length; i += batchSize) {
      const batch = titleProducts.slice(i, i + batchSize)
      try {
        const data = await api.post('/api/inventory/title-analyse', { products: batch })
        if (data.success && data.results) {
          const withStatus = data.results.map(r => ({ ...r, status: 'pending', edited_title: null }))
          allResults.push(...withStatus)
          setTitleResults([...allResults])
        }
      } catch (e) {
        console.error('Analyse batch error:', e)
        // Mark failed batch items as low confidence
        batch.forEach(p => {
          allResults.push({
            shopify_id: p.shopify_id,
            current_title: p.title,
            proposed_title: '',
            confidence: 'low',
            changes: 'Analysis failed: ' + e.message,
            status: 'pending',
            edited_title: null
          })
        })
        setTitleResults([...allResults])
      }
      setTitleProgress({ done: Math.min(i + batchSize, titleProducts.length), total: titleProducts.length })
    }

    setTitleAnalysing(false)
  }

  const handleTitleApprove = (shopifyId) => {
    setTitleResults(prev => prev.map(r => r.shopify_id === shopifyId ? { ...r, status: 'approved' } : r))
  }

  const handleTitleSkip = (shopifyId) => {
    setTitleResults(prev => prev.map(r => r.shopify_id === shopifyId ? { ...r, status: 'skipped' } : r))
  }

  const handleTitleEdit = (shopifyId) => {
    const item = titleResults.find(r => r.shopify_id === shopifyId)
    if (item) {
      setTitleEditingId(shopifyId)
      setTitleEditValue(item.edited_title || item.proposed_title)
    }
  }

  const handleTitleEditSave = () => {
    if (!titleEditingId) return
    setTitleResults(prev => prev.map(r =>
      r.shopify_id === titleEditingId ? { ...r, edited_title: titleEditValue, status: 'edited' } : r
    ))
    setTitleEditingId(null)
    setTitleEditValue('')
  }

  const handleTitleApproveAllHigh = () => {
    setTitleResults(prev => prev.map(r =>
      r.confidence === 'high' && r.status === 'pending' ? { ...r, status: 'approved' } : r
    ))
  }

  const handleTitleApply = async () => {
    const toApply = titleResults.filter(r => r.status === 'approved' || r.status === 'edited')
    if (!toApply.length) return alert('No approved titles to apply')

    setTitleApplying(true)
    setTitleProgress({ done: 0, total: toApply.length })

    const batchSize = 5
    let applied = 0

    for (let i = 0; i < toApply.length; i += batchSize) {
      const batch = toApply.slice(i, i + batchSize)
      const updates = batch.map(r => ({
        shopify_id: r.shopify_id,
        new_title: r.edited_title || r.proposed_title
      }))

      try {
        const data = await api.post('/api/inventory/title-apply', { updates })
        if (data.success && data.results) {
          data.results.forEach(result => {
            if (result.success) {
              setTitleResults(prev => prev.map(r =>
                r.shopify_id === result.shopify_id ? { ...r, status: 'applied' } : r
              ))
              applied++
            }
          })
        }
      } catch (e) {
        console.error('Apply batch error:', e)
      }

      setTitleProgress({ done: Math.min(i + batchSize, toApply.length), total: toApply.length })

      // 500ms delay between batches for rate limiting
      if (i + batchSize < toApply.length) {
        await new Promise(resolve => setTimeout(resolve, 500))
      }
    }

    setTitleApplying(false)
    alert(`Applied ${applied} title updates`)
  }

  if (loading && users.length === 0) {
    return (
      <div className="admin-panel">
        <div className="loading-center">
          <div className="loading-spinner" />
        </div>
      </div>
    )
  }

  return (
    <div className="admin-panel">
      {/* Tab switcher */}
      <div className="admin-tabs">
        <button
          className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`admin-tab ${activeTab === 'technicians' ? 'active' : ''}`}
          onClick={() => setActiveTab('technicians')}
        >
          Technicians
        </button>
        <button
          className={`admin-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>
        <button
          className={`admin-tab ${activeTab === 'video-assess' ? 'active' : ''}`}
          onClick={() => setActiveTab('video-assess')}
        >
          Video Assess
        </button>
        <button
          className={`admin-tab ${activeTab === 'title-cleanup' ? 'active' : ''}`}
          onClick={() => setActiveTab('title-cleanup')}
        >
          Title Cleanup
        </button>
      </div>

      {error && (
        <div className="alert alert-error">{error}</div>
      )}

      {/* ===== USERS TAB ===== */}
      {activeTab === 'users' && (
        <>
          <div className="admin-header">
            <div>
              <h1>User Management</h1>
              <p className="text-muted">Manage user accounts and permissions</p>
            </div>
            <button className="btn btn-primary" onClick={() => setCreatingUser(true)}>
              + Add User
            </button>
          </div>

          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Employee ID</th>
                  <th>Name</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.user_id} style={!u.is_active ? { opacity: 0.6 } : undefined}>
                    <td>
                      <span className="badge badge-neutral" style={{ fontFamily: 'monospace', fontWeight: 600 }}>{u.employee_id}</span>
                    </td>
                    <td>
                      <div style={{ fontWeight: 500 }}>{u.name}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {getRoleNames(u).map(name => (
                          <span key={name} className="badge badge-info">{name}</span>
                        ))}
                      </div>
                    </td>
                    <td>
                      {u.is_active ? (
                        <span className="badge badge-success">Active</span>
                      ) : (
                        <span className="badge badge-neutral">Inactive</span>
                      )}
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => setEditingUser(u)}
                        >
                          Edit
                        </button>
                        {!u.is_admin && (
                          <>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleViewPin(u.user_id, u.name)}
                            >
                              View PIN
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={() => handleResetPin(u.user_id, u.name)}
                            >
                              Reset PIN
                            </button>
                          </>
                        )}
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleUnlock(u.user_id, u.name)}
                        >
                          Unlock
                        </button>
                        {u.is_active && (
                          <button
                            className="btn btn-sm btn-ghost"
                            onClick={() => handleDisable(u.user_id, u.name)}
                          >
                            Deactivate
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ===== TECHNICIANS TAB ===== */}
      {activeTab === 'technicians' && (
        <>
          <div className="admin-header">
            <div>
              <h1>Technician Management</h1>
              <p className="text-muted">Manage repair technicians and their product type qualifications</p>
            </div>
            <button className="btn btn-primary" onClick={() => openTechForm(null)}>
              + Add Technician
            </button>
          </div>

          {techLoading ? (
            <div className="loading-center"><div className="loading-spinner" /></div>
          ) : (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Product Types</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {technicians.length === 0 ? (
                    <tr><td colSpan={5} className="text-muted" style={{ textAlign: 'center', padding: 'var(--space-6)' }}>No technicians yet. Click "+ Add Technician" to create one.</td></tr>
                  ) : technicians.map(tech => (
                    <tr key={tech.id} style={!tech.active ? { opacity: 0.6 } : undefined}>
                      <td><div style={{ fontWeight: 500 }}>{tech.name}</div></td>
                      <td><span className="text-muted text-sm">{tech.email || '-'}</span></td>
                      <td>
                        <div className="tech-product-types">
                          {(tech.product_types || []).length === 0 ? (
                            <span className="text-muted text-sm">None assigned</span>
                          ) : (
                            <>
                              <span className="badge badge-neutral">{tech.product_types.length} type{tech.product_types.length !== 1 ? 's' : ''}</span>
                              <div className="tech-types-list">
                                {tech.product_types.map(pt => (
                                  <span key={pt} className="tech-type-tag">{pt}</span>
                                ))}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        {tech.active ? (
                          <span className="badge badge-success">Active</span>
                        ) : (
                          <span className="badge badge-neutral">Inactive</span>
                        )}
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn btn-sm btn-ghost" onClick={() => openTechForm(tech)}>Edit</button>
                          <button
                            className={`btn btn-sm ${tech.active ? 'btn-ghost' : 'btn-primary'}`}
                            onClick={() => toggleTechActive(tech)}
                          >
                            {tech.active ? 'Deactivate' : 'Activate'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ===== SETTINGS TAB ===== */}
      {activeTab === 'settings' && (
        <>
          <div className="admin-header">
            <div>
              <h1>Settings</h1>
              <p className="text-muted">Pricing worker configuration</p>
            </div>
          </div>

          {settingsLoading ? (
            <div className="loading-center"><div className="loading-spinner" /></div>
          ) : (
            <div className="card" style={{ padding: 'var(--space-5)' }}>
              <div className="settings-section">
                <h3 className="settings-section-title">Vision Engine</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 'var(--space-3)' }}>
                  Select which AI engine identifies products from images. Claude handles all downstream processing regardless.
                </p>
                <div className="vision-toggle">
                  <button
                    className={`vision-option ${getSettingValue('vision_engine', 'claude') === 'claude' ? 'active' : ''}`}
                    disabled={settingsSaving}
                    onClick={() => updateSetting('vision_engine', 'claude')}
                  >
                    <span className="vision-option-name">Claude Vision</span>
                    <span className="vision-option-desc">Anthropic Sonnet</span>
                  </button>
                  <button
                    className={`vision-option ${getSettingValue('vision_engine', 'claude') === 'openai' ? 'active' : ''}`}
                    disabled={settingsSaving}
                    onClick={() => updateSetting('vision_engine', 'openai')}
                  >
                    <span className="vision-option-name">GPT-4o Vision</span>
                    <span className="vision-option-desc">OpenAI</span>
                  </button>
                </div>
                {settingsSaving && <p className="text-muted text-sm" style={{ marginTop: 'var(--space-2)' }}>Saving...</p>}
              </div>

              <div className="settings-section" style={{ marginTop: 'var(--space-4)' }}>
                <h3 className="settings-section-title">OCR Engine</h3>
                <p className="text-muted text-sm" style={{ marginBottom: 'var(--space-3)' }}>
                  Select which engine extracts text from product images (nameplates, serial numbers, labels).
                </p>
                <div className="vision-toggle">
                  <button
                    className={`vision-option ${getSettingValue('ocr_engine', 'azure') === 'azure' ? 'active' : ''}`}
                    disabled={settingsSaving}
                    onClick={() => updateSetting('ocr_engine', 'azure')}
                  >
                    <span className="vision-option-name">Azure OCR</span>
                    <span className="vision-option-desc">Computer Vision API</span>
                  </button>
                  <button
                    className={`vision-option ${getSettingValue('ocr_engine', 'azure') === 'gpt' ? 'active' : ''}`}
                    disabled={settingsSaving}
                    onClick={() => updateSetting('ocr_engine', 'gpt')}
                  >
                    <span className="vision-option-name">GPT-4o OCR</span>
                    <span className="vision-option-desc">OpenAI Vision</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== VIDEO ASSESS TAB ===== */}
      {activeTab === 'video-assess' && (
        <>
          <div className="admin-header">
            <div>
              <h1>Video Assessment Prototype</h1>
              <p className="text-muted">Upload a video to identify and price items from keyframes</p>
            </div>
            {(videoFile || keyframes.length > 0 || assessmentResult) && (
              <button className="btn btn-secondary" onClick={resetVideoAssess}>
                Reset
              </button>
            )}
          </div>

          {/* Upload Section */}
          <div className="card" style={{ padding: 'var(--space-5)', marginBottom: 'var(--space-4)' }}>
            <div style={{ display: 'flex', gap: 'var(--space-4)', alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div className="form-group" style={{ margin: 0, flex: 1, minWidth: 200 }}>
                <label className="filter-label">Video File</label>
                <input
                  type="file"
                  accept="video/*"
                  className="form-input"
                  onChange={handleVideoUpload}
                  disabled={compressing || extractingFrames || assessing}
                />
              </div>
              <div className="form-group" style={{ margin: 0, width: 120 }}>
                <label className="filter-label">Keyframes</label>
                <select
                  className="form-input"
                  value={numKeyframes}
                  onChange={e => setNumKeyframes(parseInt(e.target.value))}
                  disabled={compressing || extractingFrames || assessing}
                >
                  <option value={4}>4</option>
                  <option value={6}>6</option>
                  <option value={8}>8</option>
                  <option value={12}>12</option>
                  <option value={16}>16</option>
                </select>
              </div>
            </div>

            {/* Progress */}
            {(compressing || extractingFrames || assessing) && (
              <div style={{ marginTop: 'var(--space-3)' }}>
                {compressing && (
                  <div className="va-progress-bar">
                    <div className="va-progress-fill" style={{ width: compressProgress + '%' }} />
                  </div>
                )}
                <p className="text-muted text-sm" style={{ margin: 'var(--space-2) 0 0' }}>
                  {assessing && <span className="loading-spinner" style={{ width: 14, height: 14, display: 'inline-block', verticalAlign: 'middle', marginRight: 8 }} />}
                  {videoStatus}
                </p>
              </div>
            )}
            {!compressing && !extractingFrames && !assessing && videoStatus && (
              <p className="text-muted text-sm" style={{ margin: 'var(--space-2) 0 0' }}>{videoStatus}</p>
            )}
          </div>

          {/* Keyframe Grid */}
          {keyframes.length > 0 && (
            <div className="card" style={{ padding: 'var(--space-5)', marginBottom: 'var(--space-4)' }}>
              <h3 style={{ margin: '0 0 var(--space-3)', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>
                Extracted Keyframes ({keyframes.length})
              </h3>
              <div className="va-keyframe-grid">
                {keyframes.map((frame, i) => (
                  <div key={i} className="va-keyframe" onClick={() => setLightboxFrame(i)} style={{ cursor: 'pointer' }}>
                    <img src={`data:image/jpeg;base64,${frame}`} alt={`Frame ${i + 1}`} />
                    <span className="va-keyframe-label">{i + 1}</span>
                  </div>
                ))}
              </div>
              {!assessmentResult && (
                <button
                  className="btn btn-primary"
                  style={{ marginTop: 'var(--space-4)' }}
                  onClick={assessFrames}
                  disabled={assessing}
                >
                  {assessing ? 'Assessing...' : 'Assess Items'}
                </button>
              )}
            </div>
          )}

          {/* Results — same layout as deal view */}
          {assessmentResult && (
            <div className="card" style={{ padding: 'var(--space-5)' }}>
              {assessmentResult.success ? (
                <>
                  {/* Parsed Items — deal-style cards */}
                  {assessmentResult.items && assessmentResult.items.length > 0 && (
                    <div className="parsed-items-list" style={{ marginBottom: 'var(--space-4)' }}>
                      {assessmentResult.items.map((item, i) => {
                        const score = item.score || null
                        return (
                          <div key={i} className="parsed-item-card">
                            <div className="parsed-item-main">
                              <span className="parsed-item-number">{item.index || i + 1}</span>
                              {score !== null && (
                                <span className={`score-badge ${score >= 150 ? 'high' : score >= 100 ? 'medium' : score >= 45 ? 'low' : 'very-low'}`}>
                                  {score}
                                </span>
                              )}
                              <span className="parsed-item-description">{item.name}</span>
                            </div>
                            <div className="parsed-item-prices">
                              <div className="price-field">
                                <span className="price-label">SP:</span>
                                <span className="price-value sp">£{item.sellPrice}</span>
                              </div>
                              <div className="price-field">
                                <span className="price-label">BP:</span>
                                <span className="price-value bp">£{item.buyPrice}</span>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}

                  {/* Condition + Confidence badges */}
                  <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-4)', flexWrap: 'wrap', alignItems: 'center' }}>
                    {assessmentResult.condition && (
                      <span className={`badge ${
                        assessmentResult.condition.assessed === 'Excellent' ? 'badge-success' :
                        assessmentResult.condition.assessed === 'Very Good' ? 'badge-success' :
                        assessmentResult.condition.assessed === 'Good' ? 'badge-info' :
                        assessmentResult.condition.assessed === 'Fair' ? 'badge-warning' :
                        'badge-neutral'
                      }`} title={assessmentResult.condition.reasoning || ''}>
                        Condition: {assessmentResult.condition.assessed}
                      </span>
                    )}
                    {assessmentResult.confidence && (
                      <>
                        <span className="badge badge-info">IC: {assessmentResult.confidence.ic?.toFixed(2)}</span>
                        <span className="badge badge-info">MC: {assessmentResult.confidence.mc?.toFixed(2)}</span>
                        <span className={`badge ${assessmentResult.confidence.fc >= 0.6 ? 'badge-success' : 'badge-warning'}`}>
                          FC: {assessmentResult.confidence.fc?.toFixed(2)} ({assessmentResult.confidence.action})
                        </span>
                      </>
                    )}
                  </div>
                  {assessmentResult.condition?.reasoning && (
                    <p className="text-muted text-sm" style={{ marginTop: '-var(--space-2)', marginBottom: 'var(--space-4)' }}>
                      {assessmentResult.condition.reasoning}
                    </p>
                  )}

                  {/* Raw parsed_items */}
                  {assessmentResult.parsed_items && (
                    <div style={{ marginBottom: 'var(--space-4)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                        <label className="filter-label" style={{ margin: 0 }}>Raw parsed_items</label>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => {
                            navigator.clipboard.writeText(assessmentResult.parsed_items)
                            alert('Copied to clipboard')
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <pre className="va-raw-output">{assessmentResult.parsed_items}</pre>
                    </div>
                  )}

                  {/* Pricing Log — same layout as deal view */}
                  {assessmentResult.pricing_log && (
                    <div className="pricing-log-section">
                      <div className="pricing-log-header">
                        <strong>Pricing Log:</strong>
                        {(() => {
                          const badge = getValidationBadge(assessmentResult.pricing_log)
                          return badge ? <span className={badge.className}>{badge.label}</span> : null
                        })()}
                        <button
                          className="btn btn-xs btn-secondary"
                          onClick={() => {
                            navigator.clipboard.writeText(assessmentResult.pricing_log)
                            alert('Pricing log copied to clipboard')
                          }}
                        >
                          Copy
                        </button>
                      </div>
                      <div className="pricing-log">
                        {parsePricingLogSections(assessmentResult.pricing_log).map((sec, idx) => (
                          <div key={idx} className="pricing-log-block">
                            <div className="pricing-log-block-header">{sec.title}</div>
                            <pre className="pricing-log-block-content">{sec.content}</pre>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div>
                  <h3 style={{ margin: '0 0 var(--space-2)', color: 'var(--crimson)' }}>Assessment Failed</h3>
                  <p className="text-muted">{assessmentResult.error}</p>
                </div>
              )}
            </div>
          )}

          {/* Keyframe Lightbox */}
          {lightboxFrame !== null && keyframes[lightboxFrame] && (
            <div className="modal-overlay" onClick={() => setLightboxFrame(null)}>
              <div style={{ position: 'relative', maxWidth: '90vw', maxHeight: '90vh' }} onClick={e => e.stopPropagation()}>
                <img
                  src={`data:image/jpeg;base64,${keyframes[lightboxFrame]}`}
                  alt={`Frame ${lightboxFrame + 1}`}
                  style={{ maxWidth: '90vw', maxHeight: '85vh', borderRadius: 'var(--radius-lg)', display: 'block' }}
                />
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-3)' }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setLightboxFrame(Math.max(0, lightboxFrame - 1))}
                    disabled={lightboxFrame === 0}
                  >
                    Prev
                  </button>
                  <span style={{ color: 'white', fontWeight: 600 }}>{lightboxFrame + 1} / {keyframes.length}</span>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => setLightboxFrame(Math.min(keyframes.length - 1, lightboxFrame + 1))}
                    disabled={lightboxFrame === keyframes.length - 1}
                  >
                    Next
                  </button>
                </div>
                <button
                  className="modal-close"
                  onClick={() => setLightboxFrame(null)}
                  style={{ position: 'absolute', top: -12, right: -12 }}
                >
                  &times;
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* ===== TITLE CLEANUP TAB ===== */}
      {activeTab === 'title-cleanup' && (
        <div className="admin-section">
          <div className="section-header">
            <h3>Title Standardisation</h3>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)', margin: '4px 0 0' }}>
              Scan pre-2026 products and rewrite titles to: Brand Model - Product Type - Colour - Condition w/ Extras
            </p>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' }}>
            <button className="btn btn-primary" onClick={handleTitleScan} disabled={titleScanning || titleAnalysing || titleApplying}>
              {titleScanning ? 'Scanning...' : 'Scan Pre-2026 Titles'}
            </button>
            {titleProducts.length > 0 && !titleResults.length && (
              <button className="btn btn-primary" onClick={handleTitleAnalyse} disabled={titleAnalysing || titleApplying}>
                {titleAnalysing ? `Analysing... ${titleProgress.done}/${titleProgress.total}` : `Analyse ${titleProducts.length} Titles`}
              </button>
            )}
            {titleResults.length > 0 && (
              <>
                <button className="btn btn-ghost" onClick={handleTitleApproveAllHigh} disabled={titleApplying}>
                  Approve All High
                </button>
                <button className="btn btn-primary" onClick={handleTitleApply} disabled={titleApplying || !titleResults.some(r => r.status === 'approved' || r.status === 'edited')}>
                  {titleApplying ? `Applying... ${titleProgress.done}/${titleProgress.total}` : `Apply Approved (${titleResults.filter(r => r.status === 'approved' || r.status === 'edited').length})`}
                </button>
              </>
            )}
          </div>

          {(titleAnalysing || titleApplying) && titleProgress.total > 0 && (
            <div className="tc-progress-bar" style={{ marginBottom: '16px' }}>
              <div className="tc-progress-fill" style={{ width: `${(titleProgress.done / titleProgress.total) * 100}%` }} />
            </div>
          )}

          {titleScanning && (
            <p style={{ color: 'var(--color-text-secondary)' }}>Scanning Shopify for pre-2026 active products...</p>
          )}

          {titleProducts.length > 0 && !titleResults.length && !titleAnalysing && (
            <div className="card" style={{ padding: '16px', marginBottom: '16px' }}>
              <strong>Found {titleProducts.length} pre-2026 in-stock products.</strong>{' '}
              Click "Analyse" to generate proposed title rewrites using Claude Vision.
            </div>
          )}

          {titleResults.length > 0 && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: '30%' }}>Current Title</th>
                    <th style={{ width: '30%' }}>Proposed Title</th>
                    <th style={{ width: '8%' }}>Confidence</th>
                    <th style={{ width: '10%' }}>Status</th>
                    <th style={{ width: '22%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {titleResults.map(r => (
                    <tr key={r.shopify_id} style={{ opacity: r.status === 'skipped' ? 0.5 : 1 }}>
                      <td style={{ fontSize: 'var(--font-size-sm)' }}>{r.current_title}</td>
                      <td style={{ fontSize: 'var(--font-size-sm)' }}>
                        {titleEditingId === r.shopify_id ? (
                          <div style={{ display: 'flex', gap: '6px' }}>
                            <input
                              type="text"
                              value={titleEditValue}
                              onChange={e => setTitleEditValue(e.target.value)}
                              style={{ flex: 1, padding: '4px 8px', fontSize: 'var(--font-size-sm)' }}
                            />
                            <button className="btn btn-sm btn-primary" onClick={handleTitleEditSave}>Save</button>
                            <button className="btn btn-sm btn-ghost" onClick={() => setTitleEditingId(null)}>Cancel</button>
                          </div>
                        ) : (
                          <span>{r.edited_title || r.proposed_title}</span>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${r.confidence === 'high' ? 'badge-success' : r.confidence === 'medium' ? 'badge-warning' : 'badge-neutral'}`}>
                          {r.confidence}
                        </span>
                      </td>
                      <td>
                        <span className={`badge ${r.status === 'applied' ? 'badge-success' : r.status === 'approved' || r.status === 'edited' ? 'badge-info' : r.status === 'skipped' ? 'badge-neutral' : 'badge-warning'}`}>
                          {r.status}
                        </span>
                      </td>
                      <td>
                        {r.status !== 'applied' && (
                          <div className="action-buttons">
                            <button className="btn btn-sm btn-ghost" onClick={() => handleTitleApprove(r.shopify_id)} title="Approve" disabled={r.status === 'approved'}>
                              &#10003;
                            </button>
                            <button className="btn btn-sm btn-ghost" onClick={() => handleTitleSkip(r.shopify_id)} title="Skip" disabled={r.status === 'skipped'}>
                              &#10007;
                            </button>
                            <button className="btn btn-sm btn-ghost" onClick={() => handleTitleEdit(r.shopify_id)} title="Edit">
                              &#9998;
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <UserEditModal
          user={editingUser}
          roles={roles}
          onClose={() => setEditingUser(null)}
          onSave={() => {
            setEditingUser(null)
            loadUsers()
          }}
        />
      )}

      {/* Create User Modal */}
      {creatingUser && (
        <UserEditModal
          user={null}
          roles={roles}
          onClose={() => setCreatingUser(false)}
          onSave={(result) => {
            setCreatingUser(false)
            if (result?.pin) {
              setNewUserPin({ userName: result.name, pin: result.pin })
            }
            loadUsers()
          }}
        />
      )}

      {/* PIN Display Modal */}
      {newUserPin && (
        <div className="modal-overlay" onClick={() => setNewUserPin(null)}>
          <div className="modal-content pin-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>PIN for {newUserPin.userName}</h2>
              <button className="modal-close" onClick={() => setNewUserPin(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll pin-display-body">
              <p>Their PIN is:</p>
              <div className="pin-code">
                {newUserPin.pin}
              </div>
              <div className="pin-warning">
                Write this PIN down and share it securely with the user.
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-ghost"
                onClick={() => {
                  navigator.clipboard.writeText(newUserPin.pin)
                  alert('PIN copied to clipboard')
                }}
              >
                Copy PIN
              </button>
              <button className="btn btn-primary" onClick={() => setNewUserPin(null)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Technician Edit/Create Modal */}
      {editingTech && (
        <div className="modal-overlay" onClick={() => setEditingTech(null)}>
          <div className="modal-content" style={{ width: '100%', maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTech.id ? 'Edit Technician' : 'Add Technician'}</h2>
              <button className="modal-close" onClick={() => setEditingTech(null)}>&times;</button>
            </div>
            <div className="modal-body-scroll">
              <div className="form-group">
                <label className="filter-label">Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={techForm.name}
                  onChange={e => setTechForm({ ...techForm, name: e.target.value })}
                  placeholder="Technician name"
                />
              </div>
              <div className="form-group">
                <label className="filter-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={techForm.email}
                  onChange={e => setTechForm({ ...techForm, email: e.target.value })}
                  placeholder="Optional email"
                />
              </div>
              {editingTech.id && (
                <div className="form-group">
                  <label className="filter-label" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <input
                      type="checkbox"
                      checked={techForm.active}
                      onChange={e => setTechForm({ ...techForm, active: e.target.checked })}
                    />
                    Active
                  </label>
                </div>
              )}
              <div className="form-group">
                <label className="filter-label">Product Types ({techForm.product_types.length} selected)</label>
                <input
                  type="text"
                  className="form-input"
                  value={techSearch}
                  onChange={e => setTechSearch(e.target.value)}
                  placeholder="Search product types..."
                  style={{ marginBottom: 8 }}
                />
                <div className="tech-type-grid">
                  {filteredProductTypes.map(pt => (
                    <label key={pt} className={`tech-type-option ${techForm.product_types.includes(pt) ? 'selected' : ''}`}>
                      <input
                        type="checkbox"
                        checked={techForm.product_types.includes(pt)}
                        onChange={() => toggleProductType(pt)}
                        style={{ display: 'none' }}
                      />
                      {pt}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setEditingTech(null)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveTechnician} disabled={techSaving}>
                {techSaving ? 'Saving...' : (editingTech.id ? 'Save Changes' : 'Create Technician')}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-panel {
          padding: var(--space-4);
        }

        .admin-tabs {
          display: flex;
          gap: var(--space-1);
          margin-bottom: var(--space-4);
          border-bottom: 2px solid var(--card-border);
        }

        .admin-tab {
          padding: var(--space-2) var(--space-4);
          background: none;
          border: none;
          border-bottom: 2px solid transparent;
          margin-bottom: -2px;
          cursor: pointer;
          font-size: var(--font-size-base);
          font-weight: 500;
          color: var(--color-text-secondary);
          transition: color 0.15s, border-color 0.15s;
        }

        .admin-tab:hover {
          color: var(--color-text-primary);
        }

        .admin-tab.active {
          color: var(--blue-monday);
          border-bottom-color: var(--blue-monday);
        }

        .admin-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: var(--space-5);
        }

        .admin-header h1 {
          font-size: var(--font-size-2xl);
          font-weight: 600;
          margin: 0 0 var(--space-1) 0;
        }

        .action-buttons {
          display: flex;
          gap: var(--space-2);
          flex-wrap: wrap;
        }

        .action-buttons .btn-ghost {
          color: var(--color-text-primary);
        }

        .action-buttons .btn-ghost:hover {
          background: rgba(0, 0, 0, 0.06);
        }

        .loading-center {
          display: flex;
          justify-content: center;
          padding: var(--space-8);
        }

        .pin-modal {
          width: 100%;
          max-width: 400px;
        }

        .pin-display-body {
          text-align: center;
        }

        .form-group {
          margin-bottom: var(--space-4);
        }

        .tech-product-types {
          display: flex;
          flex-wrap: wrap;
          gap: var(--space-1);
          align-items: center;
        }

        .tech-types-list {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 4px;
        }

        .tech-type-tag {
          display: inline-block;
          padding: 2px 6px;
          background: var(--color-bg-tertiary);
          border-radius: var(--radius-sm);
          font-size: 11px;
          color: var(--color-text-secondary);
        }

        .tech-type-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          max-height: 250px;
          overflow-y: auto;
          padding: var(--space-2);
          border: 1px solid var(--card-border);
          border-radius: var(--radius-md);
          background: #fafaf9;
        }

        .tech-type-option {
          display: inline-block;
          padding: 4px 10px;
          border-radius: var(--radius-sm);
          font-size: var(--font-size-sm);
          cursor: pointer;
          background: #e8e8e8;
          color: #444;
          transition: background 0.15s, color 0.15s;
          user-select: none;
        }

        .tech-type-option:hover {
          background: #d0d0d0;
        }

        .tech-type-option.selected {
          background: var(--blue-monday);
          color: white;
        }

        .pin-code {
          font-size: 32px;
          font-weight: 700;
          font-family: monospace;
          letter-spacing: 0.2em;
          background: var(--color-bg-tertiary);
          padding: var(--space-4);
          border-radius: var(--radius-md);
          margin: var(--space-4) 0;
        }

        .pin-warning {
          background: rgb(255 212 77 / 0.2);
          color: #b89a2e;
          padding: var(--space-3);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          margin-bottom: var(--space-4);
        }

        .alert {
          padding: var(--space-3) var(--space-4);
          border-radius: var(--radius-md);
          margin-bottom: var(--space-4);
        }

        .alert-error {
          background: rgb(229 98 94 / 0.15);
          color: var(--crimson);
        }

        .settings-section {
          margin-bottom: var(--space-5);
        }

        .settings-section-title {
          font-size: var(--font-size-lg);
          font-weight: 600;
          margin: 0 0 var(--space-1) 0;
        }

        .vision-toggle {
          display: flex;
          gap: var(--space-3);
        }

        .vision-option {
          flex: 1;
          padding: var(--space-4);
          border: 2px solid var(--card-border);
          border-radius: var(--radius-lg);
          background: var(--color-bg-primary);
          cursor: pointer;
          transition: border-color 0.15s, background 0.15s;
          text-align: center;
          display: flex;
          flex-direction: column;
          gap: var(--space-1);
        }

        .vision-option:hover:not(:disabled) {
          border-color: var(--blue-monday);
        }

        .vision-option.active {
          border-color: var(--blue-monday);
          background: rgba(78, 128, 152, 0.08);
        }

        .vision-option:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .vision-option-name {
          font-weight: 600;
          font-size: var(--font-size-base);
          color: var(--color-text-primary);
        }

        .vision-option-desc {
          font-size: var(--font-size-sm);
          color: var(--color-text-secondary);
        }

        .va-progress-bar {
          height: 6px;
          background: var(--color-bg-tertiary);
          border-radius: 3px;
          overflow: hidden;
          margin-top: var(--space-3);
        }

        .va-progress-fill {
          height: 100%;
          background: var(--blue-monday);
          transition: width 0.3s;
          border-radius: 3px;
        }

        .va-keyframe-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
          gap: var(--space-2);
        }

        .va-keyframe {
          position: relative;
          border-radius: var(--radius-md);
          overflow: hidden;
          border: 1px solid var(--card-border);
        }

        .va-keyframe img {
          width: 100%;
          height: auto;
          display: block;
        }

        .va-keyframe-label {
          position: absolute;
          bottom: 4px;
          right: 4px;
          background: rgba(0,0,0,0.6);
          color: white;
          font-size: 10px;
          padding: 1px 5px;
          border-radius: 3px;
          font-weight: 600;
        }

        .va-raw-output {
          background: var(--color-bg-tertiary);
          padding: var(--space-3);
          border-radius: var(--radius-md);
          font-size: var(--font-size-sm);
          font-family: monospace;
          white-space: pre-wrap;
          word-break: break-word;
          margin: 0;
          border: 1px solid var(--card-border);
        }

        .tc-progress-bar {
          height: 6px;
          background: var(--color-bg-tertiary);
          border-radius: 3px;
          overflow: hidden;
        }
        .tc-progress-fill {
          height: 100%;
          background: var(--blue-monday);
          transition: width 0.3s;
        }
      `}</style>
    </div>
  )
}

export default AdminPanel
